import { isIntake } from 'app/copied/constants/common';
import { ChatModuleType } from 'app/copied/models/ChatModules';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useExternalId from 'app/hooks/useExternalId';
import { useLiveChatModuleAvailable } from 'app/hooks/useLiveChatModuleAvailable';
import useModule from 'app/hooks/useModule';
import { useOfflineModulesAvailable } from 'app/hooks/useOfflineModulesAvailable';
import usePdfViewerEnabled from 'app/modules/tailor/hooks/usePdfViewerEnabled';
import { useCallback } from 'react';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';

import { EventCaptureHandler } from './types';
import useActiveScreen from './useActiveScreen';
import useTopicSource from './useTopicSource';

// is required from data team, to be empty
const optionalParams = isIntake ? {} : { flowID: '', pageName: '' };

const useEventCaptureDecorators = () => {
  const { data: helpCenter } = useFetchHelpCenter();
  const { halfOpen } = useTailorHalfOpen();
  const offlineModulesAvailable = useOfflineModulesAvailable();
  const liveChatAvailable = useLiveChatModuleAvailable();
  const vaModule = useModule(ChatModuleType.VirtualAssistant);
  const { activeScreen, previousScreen } = useActiveScreen();
  const topicSource = useTopicSource();
  const externalId = useExternalId();

  const NNOnline = !!helpCenter?.nurses_are_online;
  const vaAsLandingPageEnabled = vaModule?.va_as_landing_page_enabled;
  const aiChatEnabled = vaModule?.va_convo_ai_enabled;
  const pdfViewerEnabled = usePdfViewerEnabled();
  const initialView =
    liveChatAvailable && !offlineModulesAvailable
      ? 'LiveChat'
      : vaAsLandingPageEnabled
        ? 'VirtualAssistant'
        : 'LandingPage';

  const withActiveScreen = useCallback(
    (eventCaptureHandler: EventCaptureHandler): EventCaptureHandler => {
      return (eventName: string, params?: object) => {
        eventCaptureHandler(eventName, {
          ...params,
          ...optionalParams,
          view: activeScreen,
          previousView: previousScreen,
          aiChatEnabled,
          externalId,
          halfOpen,
          initialView,
          NNOnline,
          pdfViewerEnabled,
        });
      };
    },
    [NNOnline, activeScreen, aiChatEnabled, externalId, halfOpen, initialView, pdfViewerEnabled, previousScreen]
  );

  const withTopicSource = useCallback(
    (eventCaptureHandler: EventCaptureHandler): EventCaptureHandler => {
      return (eventName: string, params?: object) => {
        eventCaptureHandler(eventName, {
          ...params,
          ...optionalParams,
          topicSource,
          aiChatEnabled,
          externalId,
          halfOpen,
          initialView,
          NNOnline,
          pdfViewerEnabled,
        });
      };
    },
    [NNOnline, aiChatEnabled, externalId, halfOpen, initialView, pdfViewerEnabled, topicSource]
  );

  const withTopicSourceAndActiveScreen = useCallback(
    (eventCaptureHandler: EventCaptureHandler): EventCaptureHandler => {
      return (eventName: string, params?: object) => {
        eventCaptureHandler(eventName, {
          ...params,
          ...optionalParams,
          view: activeScreen,
          previousView: previousScreen,
          topicSource,
          aiChatEnabled,
          externalId,
          halfOpen,
          initialView,
          NNOnline,
          pdfViewerEnabled,
        });
      };
    },
    [
      NNOnline,
      activeScreen,
      aiChatEnabled,
      externalId,
      halfOpen,
      initialView,
      pdfViewerEnabled,
      previousScreen,
      topicSource,
    ]
  );

  return {
    withActiveScreen,
    withTopicSource,
    withTopicSourceAndActiveScreen,
  };
};

export default useEventCaptureDecorators;
